import type {
    BalanceCheck,
    LocaleCode,
    PlausibilityWarning,
    StatementLine,
    StatementLineAccount,
    StatementWindow,
    UnmappedAccount,
} from '@/api/models';
import { ASSETS_NE_EQLIAB } from '@/api/models';
import type { ApiResponse, ErrorResponse } from '@/api/handlers/responses';
import { formatGel } from '@/utils/money';
import { cn } from '@/utils/cnUtils';

/** En dash — marks an empty / nil amount cell. */
export const NIL_AMOUNT = '–';

const SUPPORTED_LOCALES: LocaleCode[] = ['ka', 'en'];

/** Resolve the app language to a supported statement locale (fallback `en`). */
export const resolveLocale = (language: string): LocaleCode =>
    (SUPPORTED_LOCALES as string[]).includes(language)
        ? (language as LocaleCode)
        : 'en';

const DEFAULT_LOCALE: LocaleCode = 'en';

/**
 * Resolve a localized label through the same fallback chain as account names:
 * requested locale → default (`en`) → first non-empty entry → `fallback`. Blank
 * strings are treated as absent.
 *
 * **Statements no longer use this** — the API resolves every statement label
 * server-side for the requested locale. It stays here because the register
 * domains (fixed assets, salaries, dividends) still carry the names they *own*
 * — an asset, an employee, a shareholder — as locale maps rather than
 * pre-resolved strings, and import it from this module. It belongs in a shared
 * util now that statements are not its caller.
 */
export const resolveLabel = (
    label: Partial<Record<LocaleCode, string>>,
    locale: LocaleCode,
    fallback = '',
): string => {
    const requested = label[locale]?.trim();
    if (requested) {
        return requested;
    }
    const fallbackLocale = label[DEFAULT_LOCALE]?.trim();
    if (fallbackLocale) {
        return fallbackLocale;
    }
    const firstPopulated = Object.values(label)
        .map((value) => value?.trim())
        .find((value) => value);
    return firstPopulated ?? fallback;
};

/** Indentation scale — reuses the Chart of Accounts 28px-per-depth step. */
export const STATEMENT_INDENT_STEP_PX = 28;
const STATEMENT_BASE_PAD_PX = 12;
export const indentForDepth = (depth: number): number =>
    STATEMENT_BASE_PAD_PX + depth * STATEMENT_INDENT_STEP_PX;

/**
 * How heavily a row reads. Derived **only** from data the API supplies —
 * `kind`, `depth`, and how many root lines the statement has — never from a
 * label or a line id, so a re-seeded layout restyles itself correctly.
 *
 * - `RESULT` — the statement's bottom line. A statement with exactly one depth-0
 *   line has that line as its payoff (the P&L's net result); a Balance Sheet has
 *   two roots and so has no single payoff row.
 * - `TOTAL` — a depth-0 subtotal in a statement with several roots.
 * - `DERIVED` — a `COMPUTED` figure nested inside a section (the
 *   result-for-period sitting inside equity).
 * - `SUBTOTAL` — a rollup below the root.
 * - `LINE` — an ordinary account-composed line.
 */
type RowWeight = 'RESULT' | 'TOTAL' | 'DERIVED' | 'SUBTOTAL' | 'LINE';

const rowWeight = (line: StatementLine, rootCount: number): RowWeight => {
    if (line.depth === 0 && rootCount === 1) {
        return 'RESULT';
    }
    if (line.kind === 'COMPUTED') {
        return 'DERIVED';
    }
    if (line.kind === 'SUBTOTAL') {
        return line.depth === 0 ? 'TOTAL' : 'SUBTOTAL';
    }
    return 'LINE';
};

const labelClassName = (weight: RowWeight): string => {
    switch (weight) {
        case 'RESULT':
            return 'text-base font-bold text-foreground';
        case 'TOTAL':
        case 'DERIVED':
            return 'font-semibold text-foreground';
        case 'SUBTOTAL':
            return 'font-medium text-foreground';
        default:
            return 'text-foreground';
    }
};

const rowClassName = (weight: RowWeight): string => {
    switch (weight) {
        case 'RESULT':
            return 'border-y-2 border-foreground/60 bg-surface-secondary';
        case 'TOTAL':
            return 'border-t-2 border-foreground/60';
        case 'DERIVED':
        case 'SUBTOTAL':
            return 'border-t border-separator';
        default:
            return '';
    }
};

/**
 * **The statement sign convention (BOS-163).** One rule, both statements,
 * stated in the footer so a reader does not have to infer it.
 *
 * Before this, three things said "reduces the total" at once and none of them
 * agreed: cost of goods sold printed unsigned and black, its subtotal printed
 * `₾ −35.60` in red, and the Δ beside them printed `+₾ 35.60`. A reader could
 * not tell whether red meant *bad*, *credit*, or *subtracted*.
 *
 * - **A line that deducts is written in parentheses** — `(₾ 35.60)` — in the
 *   ordinary text colour. That is the accounting convention, and it says
 *   "subtracted" without also saying "wrong". `sign === -1` is the server's own
 *   flag for such a line (a contra account, an expense block netted into its
 *   parent), so nothing here guesses.
 * - **Red is reserved for a figure that is wrong or bad**: an account whose
 *   balance sits on the wrong side (an asset in credit), and a negative result.
 *   It never marks an ordinary deduction.
 * - **Subtotals are unsigned**; the label carries the meaning ("Cost of sales",
 *   "Less: accumulated depreciation").
 *
 * Input VAT (3340) stays where the accountant put it — a contra inside current
 * liabilities, per `chart-of-accounts-spec.md` R5 — and is presented as a
 * deduction rather than moved to assets. Layout is accountant-owned; this is a
 * presentation rule.
 */
export type AmountTone =
    'normal' | 'deduction' | 'abnormal' | 'positive' | 'nil';

/** How a line's own figure should read, before any formatting. */
export const amountTone = (
    line: StatementLine,
    weight: RowWeight,
): AmountTone => {
    if (line.amountTetri === 0) {
        return 'nil';
    }
    if (line.sign === -1) {
        // A deduction. Negative *within* a deduction line means the deduction
        // itself runs backwards, which is abnormal rather than ordinary.
        return line.amountTetri < 0 ? 'abnormal' : 'deduction';
    }
    if (line.amountTetri < 0) {
        return 'abnormal';
    }
    return weight === 'RESULT' || weight === 'DERIVED' ? 'positive' : 'normal';
};

/**
 * A figure written under the convention: a deduction in parentheses, anything
 * else plain. `formatGel` still owns the number itself.
 */
export const formatConventional = (tetri: number, tone: AmountTone): string =>
    tone === 'deduction' ? `(${formatGel(Math.abs(tetri))})` : formatGel(tetri);

const TONE_CLASS: Record<AmountTone, string> = {
    nil: 'text-muted',
    // A deduction is ordinary. It reads as a deduction because of the brackets,
    // not because of a colour that also means "something is wrong".
    deduction: 'text-foreground',
    abnormal: 'text-danger',
    positive: 'text-success',
    normal: 'text-foreground',
};

const amountClassName = (weight: RowWeight, tone: AmountTone): string => {
    const font =
        weight === 'RESULT' || weight === 'TOTAL'
            ? 'font-bold'
            : weight === 'SUBTOTAL' || weight === 'DERIVED'
              ? 'font-medium'
              : 'font-normal';

    return cn('text-right tabular-nums', font, TONE_CLASS[tone]);
};

/**
 * Format the server's delta, under the same convention as the line it belongs to
 * (BOS-163).
 *
 * The magnitude and its direction are the API's: current − prior, in the line's
 * own natural terms, with the presentation sign excluded. The client neither
 * recomputes nor re-signs it — doing either is what made accumulated
 * depreciation growing from 11,577 to 19,000 read as a decrease.
 *
 * What changed is the *presentation*. A Δ on a deduction line is written in
 * parentheses like the line above it, so accumulated depreciation growing by 100
 * reads `(+₾ 100.00)` — "the deduction got bigger" — instead of `+₾ 100.00`
 * sitting beside `₾ −100.00` and appearing to contradict it. An explicit `+` on
 * a rise makes the direction legible without colour, and without implying the
 * rise is good or bad: more depreciation is neither.
 */
export const formatDelta = (
    deltaTetri: number | null,
    tone: AmountTone = 'normal',
): string => {
    if (deltaTetri === null || deltaTetri === 0) {
        return NIL_AMOUNT;
    }
    const signed = `${deltaTetri > 0 ? '+' : '-'}${formatGel(Math.abs(deltaTetri))}`;
    return tone === 'deduction' ? `(${signed})` : signed;
};

/** A render-ready statement row — all derivation done here, none in the View. */
export type StatementRow = {
    line: StatementLine;
    label: string;
    /** Account code, shown before the name on a single-account line. */
    code: string | null;
    /**
     * The accounts this line drills into. **Empty means no drill affordance** —
     * `SUBTOTAL` and `COMPUTED` lines have no composition, and an affordance that
     * goes nowhere is the dead link this integration removed.
     */
    drillAccounts: StatementLineAccount[];
    /** 1-based hierarchy level for `aria-level`. */
    ariaLevel: number;
    paddingInlineStart: number;
    amountText: string;
    /** How the figure reads under the convention — see {@link amountTone}. */
    tone: AmountTone;
    /** Comparative figure (en dash when there is none). */
    priorText: string;
    /** The server's delta (en dash when there is none, or when unchanged). */
    deltaText: string;
    hasWarning: boolean;
    rowClassName: string;
    labelClassName: string;
    amountClassName: string;
    priorClassName: string;
    deltaClassName: string;
};

/**
 * Turn the server's line list into render-ready rows. Pure — the renderer just
 * maps these to table rows.
 *
 * Order is the server's order, in place; depth is the server's depth. No line is
 * added, removed, reordered, re-summed or re-signed here. The only arithmetic is
 * multiplying the amount and the comparative by `sign` for display — the delta is
 * passed through untouched.
 */
export const toStatementRows = (
    lines: StatementLine[],
    warningLineIds: ReadonlySet<string>,
): StatementRow[] => {
    const rootCount = lines.filter((line) => line.depth === 0).length;

    return lines.map((line) => {
        const weight = rowWeight(line, rootCount);
        const tone = amountTone(line, weight);
        // Presented under the convention: a deduction is bracketed, so the
        // figure inside the brackets is the magnitude rather than a negative.
        const presented =
            tone === 'deduction'
                ? line.amountTetri
                : line.sign * line.amountTetri;
        const presentedPrior =
            line.priorAmountTetri === null
                ? null
                : tone === 'deduction'
                  ? line.priorAmountTetri
                  : line.sign * line.priorAmountTetri;
        const single = line.accounts.length === 1 ? line.accounts[0] : null;

        return {
            line,
            label: line.label,
            code: single?.accountCode ?? null,
            drillAccounts: line.accounts,
            ariaLevel: line.depth + 1,
            paddingInlineStart: indentForDepth(line.depth),
            tone,
            amountText:
                tone === 'nil'
                    ? NIL_AMOUNT
                    : formatConventional(presented, tone),
            priorText:
                presentedPrior === null || presentedPrior === 0
                    ? NIL_AMOUNT
                    : formatConventional(presentedPrior, tone),
            deltaText: formatDelta(line.deltaTetri, tone),
            hasWarning: warningLineIds.has(line.id),
            rowClassName: rowClassName(weight),
            labelClassName: labelClassName(weight),
            amountClassName: amountClassName(weight, tone),
            priorClassName: 'text-right tabular-nums text-muted',
            deltaClassName: 'text-right tabular-nums text-muted',
        };
    });
};

/** Line ids that carry an advisory notice (for the inline marker). */
export const warningLineIdSet = (
    warnings: PlausibilityWarning[],
): Set<string> =>
    new Set(
        warnings
            .map((warning) => warning.lineId)
            .filter((lineId): lineId is string => lineId !== null),
    );

// ── column headers, from the server's windows ───────────────────────────────

/**
 * A window described for display, in the app's `DD/MM/YYYY` format.
 *
 * `null` when the server sent no usable window — a column that cannot be
 * labelled honestly falls back to a bare "Amount" rather than borrowing wording
 * from the other statement.
 */
export type DescribedWindow =
    | { at: string; from?: undefined; to?: undefined }
    | { at?: undefined; from: string; to: string }
    | null;

/**
 * Describe what a column covers.
 *
 * This is why a Balance Sheet column is never headed "this period": the server
 * says whether the window is a position (`asOf`) or a span
 * (`dateFrom`/`dateTo`), and the header is written from that. The comparative
 * column reads `comparativeAsOf` / `comparativeDateFrom`–`To` the same way.
 */
export const describeWindow = (
    window: StatementWindow | null,
): DescribedWindow => {
    if (!window) {
        return null;
    }
    if (window.asOf) {
        return { at: isoToDisplayDate(window.asOf) };
    }
    if (window.from && window.to) {
        return {
            from: isoToDisplayDate(window.from),
            to: isoToDisplayDate(window.to),
        };
    }
    return null;
};

/** The column headers a statement renders, already localized. */
export type StatementColumns = {
    /** The current column, labelled from the statement's own window. */
    amount: string;
    /** The comparative column, or `null` when there is no comparative. */
    prior: string | null;
    /** The delta column, or `null` when there is no comparative. */
    delta: string | null;
};

// ── balance check ───────────────────────────────────────────────────────────

/**
 * The statement's root totals, in the server's order.
 *
 * Identified **structurally** — the depth-0 subtotals — not by line id or label,
 * so a re-seeded layout still resolves. A Balance Sheet has exactly two (total
 * assets, total equity & liabilities); anything else means this statement has no
 * two-sided check to show, and {@link toBalanceCheck} returns `null` rather than
 * guessing which rows to compare.
 */
export const rootTotals = (lines: StatementLine[]): StatementLine[] =>
    lines.filter((line) => line.depth === 0 && line.kind === 'SUBTOTAL');

/** A balance check ready to render, both sides labelled by the server. */
export type BalanceCheckView = {
    balanced: boolean;
    leftLabel: string;
    leftText: string;
    rightLabel: string;
    rightText: string;
    differenceText: string;
};

/** The two-total comparison itself. */
export const computeBalanceCheck = (
    assetsTetri: number,
    equityLiabilitiesTetri: number,
): BalanceCheck => {
    const differenceTetri = assetsTetri - equityLiabilitiesTetri;
    return {
        assetsTetri,
        equityLiabilitiesTetri,
        differenceTetri,
        balanced: differenceTetri === 0,
    };
};

/**
 * Compare the statement's two root totals.
 *
 * A comparison of two **server-reported** figures, not a recomputation of the
 * statement — the same shape as the journal entry form's balance indicator. Both
 * labels come from the lines themselves, so the bar cannot disagree with the rows
 * above it, and a re-labelled or re-ordered layout relabels the bar with it.
 *
 * The server has its own verdict on this in the `ASSETS_NE_EQLIAB` plausibility
 * rule, which surfaces in the advisory banner. Both read the same two figures, so
 * a disagreement would be a server-side inconsistency, not something for the
 * client to arbitrate.
 */
export const toBalanceCheck = (
    lines: StatementLine[],
): BalanceCheckView | null => {
    const roots = rootTotals(lines);
    if (roots.length !== 2) {
        return null;
    }
    const [left, right] = roots as [StatementLine, StatementLine];
    const check = computeBalanceCheck(
        left.sign * left.amountTetri,
        right.sign * right.amountTetri,
    );
    return {
        balanced: check.balanced,
        leftLabel: left.label,
        leftText: formatGel(check.assetsTetri),
        rightLabel: right.label,
        rightText: formatGel(check.equityLiabilitiesTetri),
        differenceText: formatGel(Math.abs(check.differenceTetri)),
    };
};

/**
 * Unwrap a statement query into a `{ data, error }` pair the page triple can act
 * on: an envelope failure (`success: false`) and an unexpected query error both
 * surface as `error`, mirroring the Chart of Accounts hook.
 */
export const unwrapStatement = <T>(
    queryData: ApiResponse<T> | undefined,
    queryError: unknown,
): { data: T | null; error: ErrorResponse | null } => {
    if (queryData) {
        return queryData.success
            ? { data: queryData.response, error: null }
            : { data: null, error: queryData.error };
    }
    if (queryError) {
        return { data: null, error: queryError as ErrorResponse };
    }
    return { data: null, error: null };
};

// ── date helpers (ISO YYYY-MM-DD) ───────────────────────────────────────────
const pad = (n: number): string => n.toString().padStart(2, '0');

export const toIsoDate = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const todayIso = (): string => toIsoDate(new Date());

/** ISO `YYYY-MM-DD` → the app's `DD/MM/YYYY` display format. */
export const isoToDisplayDate = (iso: string): string => {
    const [year, month, day] = iso.split('-');
    return year && month && day ? `${day}/${month}/${year}` : iso;
};

/** A `DD/MM/YYYY HH:mm` stamp for the statement footer ("Generated …"). */
export const formatGeneratedAt = (date: Date): string => {
    const p = (n: number) => n.toString().padStart(2, '0');
    return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()} ${p(
        date.getHours(),
    )}:${p(date.getMinutes())}`;
};

/** First day of the fiscal (calendar) year containing `iso`. */
export const fiscalYearStartIso = (iso: string): string =>
    `${iso.slice(0, 4)}-01-01`;

export type PnlPreset = 'THIS_MONTH' | 'THIS_QUARTER' | 'THIS_FISCAL_YEAR';

export const PNL_PRESETS: { id: PnlPreset; labelKey: string }[] = [
    { id: 'THIS_MONTH', labelKey: 'statements.pnl.presets.month' },
    { id: 'THIS_QUARTER', labelKey: 'statements.pnl.presets.quarter' },
    { id: 'THIS_FISCAL_YEAR', labelKey: 'statements.pnl.presets.fiscalYear' },
];

export type DateRange = { from: string; to: string };

/** Resolve a P&L preset to an ISO range ending at `todayIsoValue`. */
export const presetRange = (
    preset: PnlPreset,
    todayIsoValue: string,
): DateRange => {
    const [year, month] = todayIsoValue.split('-').map(Number);
    const y = year ?? 1970;
    const m = month ?? 1;
    if (preset === 'THIS_MONTH') {
        return { from: `${y}-${pad(m)}-01`, to: todayIsoValue };
    }
    if (preset === 'THIS_QUARTER') {
        const quarterStartMonth = Math.floor((m - 1) / 3) * 3 + 1;
        return { from: `${y}-${pad(quarterStartMonth)}-01`, to: todayIsoValue };
    }
    return { from: `${y}-01-01`, to: todayIsoValue };
};

/** Which preset (if any) exactly matches a range for the given day. */
export const matchPreset = (
    range: DateRange,
    todayIsoValue: string,
): PnlPreset | null => {
    const match = PNL_PRESETS.find((preset) => {
        const candidate = presetRange(preset.id, todayIsoValue);
        return candidate.from === range.from && candidate.to === range.to;
    });
    return match?.id ?? null;
};

/** The default P&L range: current fiscal year to date. */
export const defaultPnlRange = (todayIsoValue: string): DateRange => ({
    from: fiscalYearStartIso(todayIsoValue),
    to: todayIsoValue,
});

/**
 * Group the advisories for display.
 *
 * The server sends one `UNMAPPED_ACCOUNT` warning **per unmapped postable
 * account**, and a chart that has grown past its statement layout produces
 * dozens. Rendered as-is they were joined into a single paragraph, which is how
 * thirty of them became an unreadable wall of prose that buries the one notice
 * a reader must not miss — the Balance Sheet not balancing.
 *
 * So they are collapsed into one counted line listing the codes, and every
 * other rule is passed through untouched. The balance advisory is hoisted to
 * the front: it is the only one that says a figure is wrong rather than
 * incomplete.
 *
 * This is presentation only. The condition itself is server-side — the ledger's
 * statement layout maps accounts to lines, and no client can change that.
 */
export const UNMAPPED_ACCOUNT = 'UNMAPPED_ACCOUNT';

/**
 * Advisory messages for display, in priority order.
 *
 * The server sends one `UNMAPPED_ACCOUNT` warning **per unmapped postable
 * account**, and a chart that has grown past its statement layout produces
 * dozens. Rendered as-is they were joined into a single paragraph, which is how
 * thirty of them became an unreadable wall of prose that buries the one notice
 * a reader must not miss — the Balance Sheet not balancing.
 *
 * So those are dropped here and the same fact is shown once, from the
 * statement's own `unmappedAccounts` (see `toUnmappedCodes`). The balance
 * advisory is hoisted to the front: it is the only one that says a figure is
 * wrong rather than incomplete.
 */
/**
 * One advisory, ready for the View to render.
 *
 * `localized` carries an i18n key and its parameters; `raw` carries the
 * server's own sentence, used only when this app has no text for the rule. A
 * View calls `t(key, params)` on the first and prints the second — `t` is the
 * only logic a `.tsx` is allowed, so the choice is made here.
 */
export type WarningNotice =
    | { kind: 'localized'; key: string; params: Record<string, string> }
    | { kind: 'raw'; text: string };

/** The rules this app writes itself, in the reader's language. */
const NOTICE_KEY: Record<string, string> = {
    [ASSETS_NE_EQLIAB]: 'statements.advisories.assetsNeEqLiab',
    ASSET_CREDIT_BALANCE: 'statements.advisories.assetCreditBalance',
    LIABILITY_EQUITY_DEBIT_BALANCE:
        'statements.advisories.liabilityEquityDebitBalance',
    CONTRA_EXCEEDS_PAIR: 'statements.advisories.contraExceedsPair',
};

/**
 * Which templates interpolate an amount, and therefore cannot be rendered
 * without one (BOS-171).
 *
 * Not every rule carries `amountTetri`: `ASSETS_NE_EQLIAB` never has, and the
 * deploy in front of the audit sent none at all. Interpolating a missing amount
 * into "…carrying a credit balance of {{amount}}." produces
 * "…carrying a credit balance of ." — a sentence that stops mid-thought, which
 * reads as a rendering bug rather than as a server that sent less than this
 * page hoped for. Where the amount is load-bearing and absent, the server's own
 * English sentence is the better answer: it is complete, and it names the
 * account by code.
 */
const NEEDS_AMOUNT = new Set([
    'ASSET_CREDIT_BALANCE',
    'LIABILITY_EQUITY_DEBIT_BALANCE',
    'CONTRA_EXCEEDS_PAIR',
]);

/**
 * How an account is named in an advisory: the name a reader recognises, with
 * the chart code after it for anyone reconciling against the chart.
 *
 * Falls back to the code alone when the server sent no name — better a bare
 * code than an empty pair of brackets.
 */
export const describeWarningAccount = (
    warning: PlausibilityWarning,
): string => {
    const name = warning.accountName?.trim();
    const code = warning.accountCode?.trim();
    if (name && code) {
        return `${name} (${code})`;
    }
    return name || code || '';
};

/**
 * Turn the advisories into render-ready notices (BOS-163).
 *
 * The server's own `message` is English, interpolated, and quotes the account by
 * chart code — "Asset '1120' has a credit natural balance (-100)." — which is
 * what a Georgian reader used to get on an otherwise Georgian page. A rule this
 * app recognises is written from its own locale files instead, with the account
 * **name** and the amount through `formatGel`. An unrecognised rule still shows
 * the server's sentence: half an advisory beats none.
 *
 * `UNMAPPED_ACCOUNT` is dropped, as before — the server sends one per unmapped
 * account and thirty of them buried the one notice that matters. The same fact
 * is shown once from the statement's own `unmappedAccounts`.
 *
 * The balance advisory is hoisted to the front: it is the only one that says a
 * figure is *wrong* rather than incomplete.
 */
export const toWarningNotices = (
    warnings: PlausibilityWarning[],
): WarningNotice[] => {
    const rest = warnings.filter(
        (warning) => warning.rule !== UNMAPPED_ACCOUNT,
    );

    return [
        ...rest.filter((warning) => warning.rule === ASSETS_NE_EQLIAB),
        ...rest.filter((warning) => warning.rule !== ASSETS_NE_EQLIAB),
    ]
        .map((warning): WarningNotice | null => {
            const key = NOTICE_KEY[warning.rule];
            const amountMissing =
                NEEDS_AMOUNT.has(warning.rule) && warning.amountTetri === null;

            if (key && !amountMissing) {
                return {
                    kind: 'localized',
                    key,
                    params: {
                        account: describeWarningAccount(warning),
                        amount:
                            warning.amountTetri === null
                                ? ''
                                : formatGel(warning.amountTetri),
                    },
                };
            }
            return warning.message
                ? { kind: 'raw', text: warning.message }
                : null;
        })
        .filter((notice): notice is WarningNotice => notice !== null);
};

/**
 * Codes of the accounts this statement maps nowhere, deduplicated.
 *
 * Read from the **statement**, not from the plausibility advisories that say
 * the same thing. Three reasons, all of which bite: the statement's list is
 * scoped to the statement rather than to a single date (the P&L asks for
 * plausibility at its range end, which is a different question); it carries the
 * account name and type; and the advisory call is explicitly allowed to fail
 * without failing the page — sourced from there, "this statement is incomplete"
 * would vanish exactly when the page was least sure of itself, while the
 * statement stayed just as incomplete.
 */
export const toUnmappedCodes = (accounts: UnmappedAccount[]): string[] => [
    ...new Set(accounts.map((account) => account.accountCode)),
];

// The cumulative-drill window lives at the `accounting` root: the trial
// balance needs it too, and it must not be defined twice.
export { LEDGER_START, ledgerWindowFor } from '../ledgerWindow';
